package fyi.moko.hotpl.domain.model

/**
 * Minimal Geohash encoder for coarse region partitioning.
 *
 * Produces the standard geohash string by interleaving latitude and longitude bits.
 * Precision 5 gives roughly a ~5 km x ~5 km cell, which is a good coarse partition
 * for the Yellow Page directory cache. The implementation is self-contained and
 * works across all Kotlin Multiplatform targets (commonMain).
 */
object GeohashEncoder {

    private const val BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz"

    /**
     * Approximate center of the geohash cell. Used for coarse location bias when we
     * only have a geohash prefix (e.g. 5-character region key) rather than exact
     * coordinates. Returns null for unknown/empty geohashes.
     */
    fun center(geohash: String): GeoPoint? {
        if (geohash.isBlank()) return null
        val bounds = decodeBounds(geohash)
        return GeoPoint(
            latitude = (bounds.latMin + bounds.latMax) / 2.0,
            longitude = (bounds.lonMin + bounds.lonMax) / 2.0,
        )
    }

    /**
     * Decodes a geohash into its bounding box.
     */
    private fun decodeBounds(geohash: String): LatLonBounds {
        var latMin = -90.0
        var latMax = 90.0
        var lonMin = -180.0
        var lonMax = 180.0
        var evenBit = true

        for (char in geohash) {
            val charIndex = BASE32.indexOf(char)
            if (charIndex == -1) continue
            for (bitIndex in 4 downTo 0) {
                val bitSet = (charIndex shr bitIndex) and 1 == 1
                if (evenBit) {
                    // Longitude bit
                    val mid = (lonMin + lonMax) / 2.0
                    if (bitSet) {
                        lonMin = mid
                    } else {
                        lonMax = mid
                    }
                } else {
                    // Latitude bit
                    val mid = (latMin + latMax) / 2.0
                    if (bitSet) {
                        latMin = mid
                    } else {
                        latMax = mid
                    }
                }
                evenBit = !evenBit
            }
        }
        return LatLonBounds(latMin, latMax, lonMin, lonMax)
    }

    private data class LatLonBounds(
        val latMin: Double,
        val latMax: Double,
        val lonMin: Double,
        val lonMax: Double,
    )

    /**
     * Encodes [latitude]/[longitude] into a geohash of the requested [precision].
     * Defaults to precision 9 (sub-meter), matching the field stored on [YellowPageEntry].
     */
    fun encode(latitude: Double, longitude: Double, precision: Int = 9): String {
        require(precision in 1..12) { "Geohash precision must be between 1 and 12" }

        var latMin = -90.0
        var latMax = 90.0
        var lonMin = -180.0
        var lonMax = 180.0

        val hash = CharArray(precision)
        var ch = 0
        var bit = 0
        var evenBit = true
        var index = 0

        while (index < precision) {
            if (evenBit) {
                // Longitude bit
                val mid = (lonMin + lonMax) / 2.0
                if (longitude >= mid) {
                    ch = ch or (1 shl (4 - bit))
                    lonMin = mid
                } else {
                    lonMax = mid
                }
            } else {
                // Latitude bit
                val mid = (latMin + latMax) / 2.0
                if (latitude >= mid) {
                    ch = ch or (1 shl (4 - bit))
                    latMin = mid
                } else {
                    latMax = mid
                }
            }

            evenBit = !evenBit
            if (bit < 4) {
                bit++
            } else {
                hash[index] = BASE32[ch]
                index++
                ch = 0
                bit = 0
            }
        }

        return hash.concatToString()
    }
}
